import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import CaloriesSection from 'components/nutritional_panels/editable_nutrition_facts/CaloriesSection';
import CarbohydratesSection from 'components/nutritional_panels/editable_nutrition_facts/CarbohydratesSection';
import EditMainNutritionRow from 'components/nutritional_panels/editable_nutrition_facts/EditMainNutritionRow';
import EditSubNutritionRow from 'components/nutritional_panels/editable_nutrition_facts/EditSubNutritionRow';
import FatSection from 'components/nutritional_panels/editable_nutrition_facts/FatSection';
import { fontStyles } from 'components/shared/nutrition_facts/sharedStyles';
import { NUTRITION_ATTRIBUTES } from 'components/upb_viewer/pdp_components/ProductNutritionFactsView';
import Separator, {
  MEDIUM_SEPARATOR_THICKNESS,
  LARGE_SEPARATOR_THICKNESS,
} from 'components/shared/nutrition_facts/Separator';
import ServingsSection from 'components/nutritional_panels/editable_nutrition_facts/ServingsSection';
import useFindAttributeValueByName from 'lib/hooks/product/useFindAttributeValueByName';

function updateAttributeValueByName(setAttributes, attributeName, newValue) {
  setAttributes(prevAttributes => {
    // If newValue is empty or null, filter out the attribute
    if (newValue === '' || newValue === null) {
      return prevAttributes.filter(attr => attr.attribute_name !== attributeName);
    }

    // Otherwise, update or add the attribute
    const existingAttribute = prevAttributes.find(attr => attr.attribute_name === attributeName);

    if (existingAttribute) {
      // Update existing attribute
      return prevAttributes.map(attr =>
        attr.attribute_name === attributeName ? { ...attr, attribute_value: newValue } : attr,
      );
    } else {
      // Add new attribute
      return [...prevAttributes, { attribute_name: attributeName, attribute_value: newValue }];
    }
  });
}

function ProductNutritionFactsForEdit({ nutritionAttributes, setNutritionAttributes }) {
  const servingSizeQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_QUANTITY,
  );
  const servingSizeUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_UOM,
  );
  const servingSizeDescription = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SERVING_SIZE_DESCRIPTION,
  );
  const servingsPerPackage = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NUMBER_OF_SERVINGS_PER_PACKAGE,
  );
  const calories = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALORIES_QUANTITYCONTAINED,
  );

  // Cholesterol attributes
  const cholesterolQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_QUANTITYCONTAINED,
  );
  const cholesterolUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_UOM,
  );
  const cholesterolDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const cholesterolPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CHOLESTEROL_MEASUREMENTPRECISIONCODE,
  );

  // Sodium attributes
  const sodiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_QUANTITYCONTAINED,
  );
  const sodiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_UOM,
  );
  const sodiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const sodiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SODIUM_MEASUREMENTPRECISIONCODE,
  );

  // Protein attributes
  const proteinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_QUANTITYCONTAINED,
  );
  const proteinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_UOM,
  );
  const proteinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const proteinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PROTEIN_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin D attributes
  const vitaminDQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_QUANTITYCONTAINED,
  );
  const vitaminDUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_UOM,
  );
  const vitaminDDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminDPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_D_MEASUREMENTPRECISIONCODE,
  );

  // Calcium attributes
  const calciumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_QUANTITYCONTAINED,
  );
  const calciumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_UOM,
  );
  const calciumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const calciumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CALCIUM_MEASUREMENTPRECISIONCODE,
  );

  // Iron attributes
  const ironQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_QUANTITYCONTAINED,
  );
  const ironUom = useFindAttributeValueByName(nutritionAttributes, NUTRITION_ATTRIBUTES.IRON_UOM);
  const ironDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const ironPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.IRON_MEASUREMENTPRECISIONCODE,
  );

  // Potassium attributes
  const potassiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_QUANTITYCONTAINED,
  );
  const potassiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_UOM,
  );
  const potassiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const potassiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.POTASSIUM_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin A attributes
  const vitaminAQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_QUANTITYCONTAINED,
  );
  const vitaminAUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_UOM,
  );
  const vitaminADailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminAPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_A_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin C attributes
  const vitaminCQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_QUANTITYCONTAINED,
  );
  const vitaminCUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_UOM,
  );
  const vitaminCDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminCPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_C_MEASUREMENTPRECISIONCODE,
  );

  // Thiamin attributes
  const thiaminQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_QUANTITYCONTAINED,
  );
  const thiaminUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_UOM,
  );
  const thiaminDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const thiaminPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.THIAMIN_MEASUREMENTPRECISIONCODE,
  );

  // Riboflavin attributes
  const riboflavinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_QUANTITYCONTAINED,
  );
  const riboflavinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_UOM,
  );
  const riboflavinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const riboflavinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_MEASUREMENTPRECISIONCODE,
  );

  // Niacin attributes
  const niacinQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_QUANTITYCONTAINED,
  );
  const niacinUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_UOM,
  );
  const niacinDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const niacinPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.NIACIN_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin B6 attributes
  const vitaminB6Quantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_QUANTITYCONTAINED,
  );
  const vitaminB6Uom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_UOM,
  );
  const vitaminB6DailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminB6PrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B6_MEASUREMENTPRECISIONCODE,
  );

  // Folate attributes
  const folateQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_QUANTITYCONTAINED,
  );
  const folateUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_UOM,
  );
  const folateDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const folatePrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.FOLATE_MEASUREMENTPRECISIONCODE,
  );

  // Vitamin B12 attributes
  const vitaminB12Quantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_QUANTITYCONTAINED,
  );
  const vitaminB12Uom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_UOM,
  );
  const vitaminB12DailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const vitaminB12PrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.VITAMIN_B12_MEASUREMENTPRECISIONCODE,
  );

  // Phosphorus attributes
  const phosphorousQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHORUS_QUANTITYCONTAINED,
  );
  const phosphorousUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHORUS_UOM,
  );
  const phosphorousDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHORUS_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const phosphorousPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.PHOSPHORUS_MEASUREMENTPRECISIONCODE,
  );

  // Magnesium attributes
  const magnesiumQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_QUANTITYCONTAINED,
  );
  const magnesiumUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_UOM,
  );
  const magnesiumDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const magnesiumPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.MAGNESIUM_MEASUREMENTPRECISIONCODE,
  );

  // Zinc attributes
  const zincQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_QUANTITYCONTAINED,
  );
  const zincUom = useFindAttributeValueByName(nutritionAttributes, NUTRITION_ATTRIBUTES.ZINC_UOM);
  const zincDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const zincPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ZINC_MEASUREMENTPRECISIONCODE,
  );

  if (!nutritionAttributes?.length) {
    return null;
  }

  return (
    <div className="border border-dark p-2">
      <div className={fontStyles('44px', true, true)}>Nutrition Facts</div>
      <Separator />

      <ServingsSection
        servingsPerPackage={servingsPerPackage}
        description={servingSizeDescription}
        quantity={servingSizeQuantity}
        uom={servingSizeUom}
        setNutritionAttributes={setNutritionAttributes}
      />
      <Separator thickness={LARGE_SEPARATOR_THICKNESS} />

      <CaloriesSection calories={calories} setNutritionAttributes={setNutritionAttributes} />
      <Separator thickness={MEDIUM_SEPARATOR_THICKNESS} />

      <div className={classNames('w-100 text-right', fontStyles('12px', true))}>% Daily Value*</div>
      <Separator />

      <FatSection
        nutritionAttributes={nutritionAttributes}
        setNutritionAttributes={setNutritionAttributes}
      />

      <EditMainNutritionRow
        title="Cholesterol"
        quantity={cholesterolQuantity}
        uom={cholesterolUom}
        percentage={cholesterolDailyValue}
        precisionCode={cholesterolPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.CHOLESTEROL_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.CHOLESTEROL_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.CHOLESTEROL_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.CHOLESTEROL_MEASUREMENTPRECISIONCODE}
      />

      <EditMainNutritionRow
        title="Sodium"
        quantity={sodiumQuantity}
        uom={sodiumUom}
        percentage={sodiumDailyValue}
        precisionCode={sodiumPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.SODIUM_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.SODIUM_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.SODIUM_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.SODIUM_MEASUREMENTPRECISIONCODE}
      />

      <CarbohydratesSection
        nutritionAttributes={nutritionAttributes}
        setNutritionAttributes={setNutritionAttributes}
      />

      <EditMainNutritionRow
        title="Protein"
        quantity={proteinQuantity}
        uom={proteinUom}
        percentage={proteinDailyValue}
        precisionCode={proteinPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.PROTEIN_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.PROTEIN_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.PROTEIN_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.PROTEIN_MEASUREMENTPRECISIONCODE}
      />

      <Separator thickness={LARGE_SEPARATOR_THICKNESS} />

      <EditSubNutritionRow
        title="Vitamin D"
        quantity={vitaminDQuantity}
        uom={vitaminDUom}
        percentage={vitaminDDailyValue}
        precisionCode={vitaminDPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.VITAMIN_D_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.VITAMIN_D_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.VITAMIN_D_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.VITAMIN_D_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Calcium"
        quantity={calciumQuantity}
        uom={calciumUom}
        percentage={calciumDailyValue}
        precisionCode={calciumPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.CALCIUM_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.CALCIUM_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.CALCIUM_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.CALCIUM_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Iron"
        quantity={ironQuantity}
        uom={ironUom}
        percentage={ironDailyValue}
        precisionCode={ironPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.IRON_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.IRON_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.IRON_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.IRON_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Potassium"
        quantity={potassiumQuantity}
        uom={potassiumUom}
        percentage={potassiumDailyValue}
        precisionCode={potassiumPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.POTASSIUM_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.POTASSIUM_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.POTASSIUM_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.POTASSIUM_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Vitamin A"
        quantity={vitaminAQuantity}
        uom={vitaminAUom}
        percentage={vitaminADailyValue}
        precisionCode={vitaminAPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.VITAMIN_A_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.VITAMIN_A_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.VITAMIN_A_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.VITAMIN_A_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Vitamin C"
        quantity={vitaminCQuantity}
        uom={vitaminCUom}
        percentage={vitaminCDailyValue}
        precisionCode={vitaminCPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.VITAMIN_C_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.VITAMIN_C_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.VITAMIN_C_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.VITAMIN_C_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Thiamin"
        quantity={thiaminQuantity}
        uom={thiaminUom}
        percentage={thiaminDailyValue}
        precisionCode={thiaminPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.THIAMIN_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.THIAMIN_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.THIAMIN_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.THIAMIN_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Riboflavin"
        quantity={riboflavinQuantity}
        uom={riboflavinUom}
        percentage={riboflavinDailyValue}
        precisionCode={riboflavinPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.RIBOFLAVIN_B2_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Niacin"
        quantity={niacinQuantity}
        uom={niacinUom}
        percentage={niacinDailyValue}
        precisionCode={niacinPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.NIACIN_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.NIACIN_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.NIACIN_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.NIACIN_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title={
          <span>
            Vitamin B<sub>6</sub>
          </span>
        }
        quantity={vitaminB6Quantity}
        uom={vitaminB6Uom}
        percentage={vitaminB6DailyValue}
        precisionCode={vitaminB6PrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B6_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B6_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B6_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B6_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Folate"
        quantity={folateQuantity}
        uom={folateUom}
        percentage={folateDailyValue}
        precisionCode={folatePrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.FOLATE_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.FOLATE_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.FOLATE_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.FOLATE_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title={
          <span>
            Vitamin B<sub>12</sub>
          </span>
        }
        quantity={vitaminB12Quantity}
        uom={vitaminB12Uom}
        percentage={vitaminB12DailyValue}
        precisionCode={vitaminB12PrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B12_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B12_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B12_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.VITAMIN_B12_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Phosphorous"
        quantity={phosphorousQuantity}
        uom={phosphorousUom}
        percentage={phosphorousDailyValue}
        precisionCode={phosphorousPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.PHOSPHOROUS_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.PHOSPHOROUS_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.PHOSPHOROUS_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.PHOSPHOROUS_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Magnesium"
        quantity={magnesiumQuantity}
        uom={magnesiumUom}
        percentage={magnesiumDailyValue}
        precisionCode={magnesiumPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.MAGNESIUM_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.MAGNESIUM_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.MAGNESIUM_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.MAGNESIUM_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Zinc"
        quantity={zincQuantity}
        uom={zincUom}
        percentage={zincDailyValue}
        precisionCode={zincPrecisionCode}
        skipSeparator
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.ZINC_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.ZINC_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.ZINC_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.ZINC_MEASUREMENTPRECISIONCODE}
      />
      <Separator thickness={MEDIUM_SEPARATOR_THICKNESS} />

      <div className={classNames(fontStyles('12px'), 'd-flex justify-content-between')}>
        <div className="pr-1">*</div>
        <div>
          The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a
          daily diet. 2,000 calories a day is used for general nutrition advice.
        </div>
      </div>
    </div>
  );
}

ProductNutritionFactsForEdit.propTypes = {
  nutritionAttributes: PropTypes.array,
  setNutritionAttributes: PropTypes.func,
};

export default ProductNutritionFactsForEdit;
export { NUTRITION_ATTRIBUTES, updateAttributeValueByName };
